// TODO: Agregar tipos correctos
// ===========================================
// BRAINHUB CORE: Provider de Organizacion
// ===========================================

import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { useSupabase } from '@/lib/supabase-context'
import { Button } from '@/components/ui/button'
import type { Organizacion, UsuarioOrganizacion, Usuario } from '../types/entities'

// ===========================================
// TIPOS DEL CONTEXTO
// ===========================================

interface OrganizacionContextType {
  // Organizacion actual
  organizacion: Organizacion | null

  // Usuario actual
  usuario: Usuario | null

  // Membresia del usuario en la organizacion
  membresia: UsuarioOrganizacion | null

  // Estado
  isLoading: boolean
  error: Error | null

  // Helpers
  organizacionId: string | null
  esOwner: boolean
  esAdmin: boolean
  workspacesHabilitados: string[]
  workspacesUsuario: string[]

  // Metodos
  refetch: () => void
}

// ===========================================
// CONTEXTO
// ===========================================

const OrganizacionContext = createContext<OrganizacionContextType | null>(null)

/** Reemplaza a la app cuando el usuario logueado está desactivado: solo deja cerrar la sesión. */
const UsuarioDesactivado = () => {
  const { logout } = useAuth0()
  return (
    <div className="flex h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
      <h1 className="text-2xl font-bold text-destructive">Usuario desactivado</h1>
      <p className="max-w-sm text-muted-foreground">
        Tu usuario está desactivado. Pedile al administrador del estudio que lo reactive.
      </p>
      <Button onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}>Cerrar sesión</Button>
    </div>
  )
}

/** Reemplaza a la app cuando no se pudo cargar al usuario: deja reintentar o cerrar la sesión. */
const ErrorDeCarga = ({ mensaje, onReintentar }: { mensaje: string; onReintentar: () => void }) => {
  const { logout } = useAuth0()
  return (
    <div className="flex h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
      <h1 className="text-2xl font-bold text-destructive">No pudimos cargar tu usuario</h1>
      <p className="max-w-sm text-muted-foreground">{mensaje}</p>
      <div className="flex gap-2">
        <Button variant="outline" onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}>Cerrar sesión</Button>
        <Button onClick={onReintentar}>Reintentar</Button>
      </div>
    </div>
  )
}

/** PostgREST rechaza con este `code` cualquier request de un usuario desactivado. */
const esUsuarioDesactivado = (error: unknown) =>
  typeof error === 'object' && error !== null && (error as { code?: unknown }).code === 'USUARIO_DESACTIVADO'

// Un rechazo por usuario desactivado no cambia al reintentar.
const reintentar = (intentos: number, falla: Error) => intentos < 1 && !esUsuarioDesactivado(falla)

// Sin `code` el error no trae respuesta del servidor, como un fetch que no llegó: su mensaje es técnico.
const mensajeDeError = (error: Error) =>
  (error as { code?: unknown }).code ? error.message : 'Revisá tu conexión e intentá de nuevo'

// ===========================================
// PROVIDER
// ===========================================

interface OrganizacionProviderProps {
  children: ReactNode
}

export function OrganizacionProvider({ children }: OrganizacionProviderProps) {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth0()
  const { supabase, isLoading: supabaseLoading, sessionExpired } = useSupabase()
  const habilitada = !!user?.sub && isAuthenticated && !!supabase

  // Obtener datos del usuario y su organizacion
  const {
    data,
    dataUpdatedAt,
    errorUpdatedAt,
    isLoading: dataLoading,
    isPending,
    error,
    refetch,
  } = useQuery({
    queryKey: ['usuario-organizacion', user?.sub],
    queryFn: async () => {
      if (!user?.sub || !user.email || !supabase) return null

      // La fila se identifica por el sub de Auth0, que la invitacion guarda al crear el usuario.
      const { data: usuarioBH, error: usuarioError } = await supabase
        .from('usuarios_brainhub')
        .select('id, email, nombre, apellido, avatar_url, preferencias, activo, ultimo_acceso, ultimo_login, created_at, updated_at, auth0_user_id')
        .eq('auth0_user_id', user.sub)
        .maybeSingle()

      if (usuarioError) throw usuarioError

      // El alta es solo por invitacion (netlify/functions/auth0-invite.ts con
      // service_role); la policy RLS usuarios_insert_service bloquea INSERT
      // desde el cliente, asi que intentarlo solo genera violaciones en prod
      if (!usuarioBH) {
        throw Object.assign(
          new Error('Tu usuario no está dado de alta en Menta. Pedile a un administrador que te invite.'),
          { code: 'USUARIO_NO_REGISTRADO' }
        )
      }

      // `activo` es nullable con default true: solo el `false` explícito es un usuario desactivado.
      // No registra el acceso, no busca la membresía y no crea una organización.
      if (usuarioBH.activo === false) {
        return { usuario: usuarioBH as Usuario, membresia: null, desactivado: true }
      }

      await supabase
        .from('usuarios_brainhub')
        .update({ ultimo_acceso: new Date().toISOString() })
        .eq('id', usuarioBH.id)

      // Buscar membresia en organizacion
      const { data: membresia, error: membresiaError } = await supabase
        .from('usuario_organizaciones')
        .select(`
          *,
          organizacion:organizaciones(*)
        `)
        .eq('usuario_id', usuarioBH.id)
        .eq('activo', true)
        .maybeSingle()

      if (membresiaError) throw membresiaError

      // Sin membresía activa no se crea nada: el alta de un estudio y de sus usuarios es por invitación.
      // Antes se creaba un estudio nuevo con rol owner, así que a alguien a quien le sacaban la membresía
      // le quedaba un estudio propio al volver a entrar.
      if (!membresia) {
        throw Object.assign(
          new Error('Tu usuario no pertenece a ningún estudio activo. Pedile a un administrador que te invite.'),
          { code: 'SIN_MEMBRESIA' }
        )
      }

      return {
        usuario: usuarioBH as Usuario,
        membresia: membresia as UsuarioOrganizacion & { organizacion: Organizacion },
      }
    },
    enabled: habilitada,
    staleTime: 5 * 60 * 1000, // Cache 5 minutos
    retry: reintentar,
  })

  // Revalida `activo` al reenfocar la pestaña y cada 5 minutos mientras está visible.
  // Lee solo esa columna: no registra el acceso ni vuelve a buscar la membresía.
  const { data: activo, error: activoError, dataUpdatedAt: activoConfirmadoEn } = useQuery({
    queryKey: ['usuario-activo', user?.sub],
    queryFn: async () => {
      const { data: fila, error: filaError } = await supabase!
        .from('usuarios_brainhub')
        .select('activo')
        .eq('auth0_user_id', user!.sub!)
        .maybeSingle()
      if (filaError) throw filaError
      return fila?.activo ?? null
    },
    enabled: habilitada && (!!data || esUsuarioDesactivado(error)),
    staleTime: 0,
    refetchOnWindowFocus: 'always',
    refetchInterval: 5 * 60_000,
    refetchIntervalInBackground: false,
    retry: reintentar,
  })

  const desactivado =
    activo === false || !!data?.desactivado || esUsuarioDesactivado(error) || esUsuarioDesactivado(activoError)

  // Un usuario reactivado sale de la pantalla de desactivado sin volver a loguearse. `NULL` cuenta como
  // habilitado, igual que en la principal. Solo cuenta un `activo` confirmado después del último resultado
  // de la principal: ante un fallo, la query conserva el valor anterior, y sin esa condición cada rechazo
  // dispararía otro refetch.
  useEffect(() => {
    const bloqueado = !!data?.desactivado || esUsuarioDesactivado(error)
    const habilitado = activo !== undefined && activo !== false
    if (habilitado && bloqueado && activoConfirmadoEn > Math.max(dataUpdatedAt, errorUpdatedAt)) void refetch()
  }, [activo, activoConfirmadoEn, data?.desactivado, dataUpdatedAt, error, errorUpdatedAt, refetch])

  // Valores calculados
  const value = useMemo<OrganizacionContextType>(() => {
    const organizacion = data?.membresia?.organizacion || null
    const usuario = data?.usuario || null
    const membresia = data?.membresia || null

    return {
      organizacion,
      usuario,
      membresia,
      isLoading: authLoading || supabaseLoading || dataLoading,
      error: error as Error | null,
      organizacionId: organizacion?.id || null,
      esOwner: membresia?.rol === 'owner',
      esAdmin: membresia?.rol === 'owner' || membresia?.rol === 'admin',
      workspacesHabilitados: organizacion?.workspaces_habilitados || ['contable'],
      workspacesUsuario: membresia?.workspaces || ['contable'],
      refetch: () => refetch(),
    }
  }, [data, authLoading, supabaseLoading, dataLoading, error, refetch])

  // Sin children no se monta nada que dependa de la organización: ni queries, ni Realtime, ni el router.
  if (desactivado) return <UsuarioDesactivado />

  // Sin sesión la query no corre y los children incluyen el login. Con la sesión vencida,
  // los children incluyen el RootGate que manda al login, aunque la query siga pendiente.
  if (habilitada && isPending && !sessionExpired) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    )
  }
  if (error && !sessionExpired) return <ErrorDeCarga mensaje={mensajeDeError(error)} onReintentar={() => void refetch()} />

  return (
    <OrganizacionContext.Provider value={value}>
      {children}
    </OrganizacionContext.Provider>
  )
}

// ===========================================
// HOOK
// ===========================================

export function useOrganizacion() {
  const context = useContext(OrganizacionContext)
  if (!context) {
    throw new Error('useOrganizacion debe usarse dentro de OrganizacionProvider')
  }
  return context
}
